import { useCallback } from "react";
import {
  useCreateFencyFileDownloadSessionMutation,
  useCreateContractSearchSessionMutation,
  useCreateExploreMemoriesSessionMutation,
  SignedDocumentDto,
  useCreateBulkFencySessionMutation,
  useCreateFencyDocumentSessionMutation,
  useCreateFencySignedDocumentSessionMutation,
  useCreateFencyStreamSessionMutation,
  useCreateMemoryChatSessionMutation,
  useFencySyncDocumentMutation,
  useFencySyncSignedDocumentMutation,
  ViewDocumentAsOwnerDto,
} from "../../autogen/bff-api";
import { isSignedDocumentDto } from "../documents/utils";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";

export const useFency = () => {
  const [createSession] = useCreateFencyStreamSessionMutation();
  const [createContractSearchSession] = useCreateContractSearchSessionMutation();
  const [createDocumentSession] = useCreateFencyDocumentSessionMutation();
  const [createSignedDocumentSession] = useCreateFencySignedDocumentSessionMutation();
  const [createBulkSession] = useCreateBulkFencySessionMutation();
  const [createMemoryChatSession] = useCreateMemoryChatSessionMutation();
  const [createExploreMemoriesSession] = useCreateExploreMemoriesSessionMutation();
  const [createFencyFileDownloadSession] = useCreateFencyFileDownloadSessionMutation();
  const [syncDocument] = useFencySyncDocumentMutation();
  const [syncSignedDocument] = useFencySyncSignedDocumentMutation();
  const authState = useLoggedInWithOrgContextState();

  const fetchCreateStreamClientToken = useCallback(async () => {
    const result = await createSession({
      organizationId: authState.selectedOrg.id,
      personId: authState.me.id,
    }).unwrap();
    return { clientToken: result.clientToken };
  }, [createSession, authState.selectedOrg.id, authState.me.id]);

  const fetchCreateMemoryChatClientToken = useCallback(
    async (document: ViewDocumentAsOwnerDto | SignedDocumentDto) => {
      const result = isSignedDocumentDto(document)
        ? await createSignedDocumentSession({
            id: document.id,
            organizationId: authState.selectedOrg.id,
            personId: authState.me.id,
          }).unwrap()
        : await createDocumentSession({
            id: document.id,
            organizationId: authState.selectedOrg.id,
            personId: authState.me.id,
          }).unwrap();
      return { clientToken: result.clientToken };
    },
    [
      createDocumentSession,
      createSignedDocumentSession,
      authState.selectedOrg.id,
      authState.me.id,
    ]
  );

  const fetchCreateBulkFencySessionClientToken = useCallback(
    async (documentIds: string[], signedDocumentIds: string[]) => {
      const result = await createBulkSession({
        createBulkFencySessionRequest: { documentIds, signedDocumentIds },
        organizationId: authState.selectedOrg.id,
        personId: authState.me.id,
      }).unwrap();
      return { clientToken: result.clientToken };
    },
    [createBulkSession, authState.selectedOrg.id, authState.me.id]
  );

  const fetchCreateContractSearchClientToken = useCallback(async () => {
    const result = await createContractSearchSession({
      organizationId: authState.selectedOrg.id,
      personId: authState.me.id,
    }).unwrap();
    return { clientToken: result.clientToken };
  }, [createContractSearchSession, authState.selectedOrg.id, authState.me.id]);

  const fetchCreateMemoryChatByMemoryIdClientToken = useCallback(
    async (memoryId: string) => {
      const result = await createMemoryChatSession({
        createMemoryChatSessionRequest: { memoryId },
        organizationId: authState.selectedOrg.id,
        personId: authState.me.id,
      }).unwrap();
      return { clientToken: result.clientToken };
    },
    [createMemoryChatSession, authState.selectedOrg.id, authState.me.id]
  );

  const fetchCreateExploreMemoriesClientToken = useCallback(
    async (conversationId: string) => {
      const result = await createExploreMemoriesSession({
        createExploreMemoriesSessionRequest: { conversationId },
        organizationId: authState.selectedOrg.id,
        personId: authState.me.id,
      }).unwrap();
      return { clientToken: result.clientToken };
    },
    [createExploreMemoriesSession, authState.selectedOrg.id, authState.me.id]
  );

  const fetchFencyFileDownloadClientToken = useCallback(
    async (agentTaskFileId: string) => {
      const result = await createFencyFileDownloadSession({
        createFencyFileDownloadSessionRequest: { agentTaskFileId },
        organizationId: authState.selectedOrg.id,
        personId: authState.me.id,
      }).unwrap();
      return { clientToken: result.clientToken };
    },
    [createFencyFileDownloadSession, authState.selectedOrg.id, authState.me.id]
  );

  const triggerSync = useCallback(
    async (document: ViewDocumentAsOwnerDto | SignedDocumentDto) => {
      if (isSignedDocumentDto(document)) {
        await syncSignedDocument({
          id: document.id,
          organizationId: authState.selectedOrg.id,
          personId: authState.me.id,
        }).unwrap();
      } else {
        await syncDocument({
          id: document.id,
          organizationId: authState.selectedOrg.id,
          personId: authState.me.id,
        }).unwrap();
      }
    },
    [syncDocument, syncSignedDocument, authState.selectedOrg.id, authState.me.id]
  );

  return {
    fetchCreateStreamClientToken,
    fetchCreateContractSearchClientToken,
    fetchCreateMemoryChatClientToken,
    fetchCreateMemoryChatByMemoryIdClientToken,
    fetchCreateBulkFencySessionClientToken,
    fetchCreateExploreMemoriesClientToken,
    fetchFencyFileDownloadClientToken,
    triggerSync,
  };
};
